import { asOrderId, asOrderSetId, ENVELOPE_DURATION_CAP_MINUTES, type OrderSetId } from "@repo/domain";
import { z } from "zod";
import { carePlanIdSchema } from "./care-plan.schemas";
import { memberIdSchema } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { parameterAnswerSchema, parameterAnswerValueSchema, plannedTrialsSchema } from "./protocol-definition.schemas";

export const orderSetIdSchema: z.ZodType<OrderSetId, z.ZodTypeDef, string> = z.string().min(1).transform(asOrderSetId);

const distinctOrderSetIds = z
  .array(orderSetIdSchema)
  .min(1)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: "Order Set ids must be unique",
  });

export const createOrderSetsInputSchema = z
  .object({
    carePlanId: carePlanIdSchema,
    /** A set naming a protocol is realized from it; one naming none starts as an empty draft. */
    orderSets: z
      .array(
        z.union([
          z
            .object({
              id: newEntityIdSchema,
              protocolUrl: z.string().url(),
              expectedVersion: z.number().int().positive(),
            })
            .strict(),
          z.object({ id: newEntityIdSchema }).strict(),
        ]),
      )
      .min(1),
    /** Index in the care plan's active curation order; absent appends. */
    position: z.number().int().nonnegative().optional(),
  })
  .strict();
export type CreateOrderSetsInput = z.infer<typeof createOrderSetsInputSchema>;

export const removeOrderSetInputSchema = z.object({ orderSetId: orderSetIdSchema }).strict();
export type RemoveOrderSetInput = z.infer<typeof removeOrderSetInputSchema>;

/** Save returns a drafted set to booking; Discard puts back what the draft replaced. */
export const orderSetDraftInputSchema = z.object({ orderSetId: orderSetIdSchema }).strict();
export type OrderSetDraftInput = z.infer<typeof orderSetDraftInputSchema>;

export const reorderOrderSetInputSchema = z
  .object({
    orderSetIds: distinctOrderSetIds,
  })
  .strict();
export type ReorderOrderSetInput = z.infer<typeof reorderOrderSetInputSchema>;

const orderSetTarget = { orderSetId: orderSetIdSchema };

export const setOrderSetEnvelopeInputSchema = z.object({
  ...orderSetTarget,
  envelopeMinutes: z.number().int().positive().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES),
});
export type SetOrderSetEnvelopeInput = z.infer<typeof setOrderSetEnvelopeInputSchema>;
export const setOrderParameterInputSchema = z.object({
  ...orderSetTarget,
  orderId: z.string().min(1).transform(asOrderId),
  source: z.union([z.literal("self"), memberIdSchema]),
  parameterId: memberIdSchema,
  value: parameterAnswerValueSchema.nullable(),
});
export type SetOrderParameterInput = z.infer<typeof setOrderParameterInputSchema>;

export const replaceOrderPlannedTrialsInputSchema = z.object({
  ...orderSetTarget,
  orderId: z.string().min(1).transform(asOrderId),
  plannedTrials: plannedTrialsSchema,
});
export type ReplaceOrderPlannedTrialsInput = z.infer<typeof replaceOrderPlannedTrialsInputSchema>;

export const addOrderInputSchema = z.object({
  ...orderSetTarget,
  orderId: z.string().min(1).transform(asOrderId),
  definitionUrl: z.string().url(),
  expectedVersion: z.number().int().positive(),
  parameterValues: z.array(parameterAnswerSchema),
  concurrentWithStep: z.number().int().nonnegative().optional(),
});
export type AddOrderInput = z.infer<typeof addOrderInputSchema>;

export const revokeOrderInputSchema = z.object({ ...orderSetTarget, orderId: z.string().min(1).transform(asOrderId) });
export type RevokeOrderInput = z.infer<typeof revokeOrderInputSchema>;

export const moveOrderInputSchema = z.object({
  ...orderSetTarget,
  orderId: z.string().min(1).transform(asOrderId),
  toStep: z.number().int().nonnegative(),
});
export type MoveOrderInput = z.infer<typeof moveOrderInputSchema>;

export const moveStepInputSchema = z.object({
  ...orderSetTarget,
  stepIndex: z.number().int().nonnegative(),
  offsetMinutes: z.number().int().nonnegative().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES),
});
export type MoveStepInput = z.infer<typeof moveStepInputSchema>;

export const resizeStepInputSchema = z.object({
  ...orderSetTarget,
  stepIndex: z.number().int().nonnegative(),
  durationMinutes: z.number().int().positive().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES),
});
export type ResizeStepInput = z.infer<typeof resizeStepInputSchema>;

export const resetTimingInputSchema = z.object(orderSetTarget).strict();
export type ResetTimingInput = z.infer<typeof resetTimingInputSchema>;

export const orderSetChangeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("reorderStep"),
    stepIndex: z.number().int().nonnegative(),
    toStep: z.number().int().nonnegative(),
  }),
  z.object({ kind: z.literal("splitOrder"), orderId: z.string().min(1).transform(asOrderId) }),
  addOrderInputSchema.omit({ orderSetId: true }).extend({ kind: z.literal("addOrder") }),
  revokeOrderInputSchema.omit({ orderSetId: true }).extend({ kind: z.literal("revokeOrder") }),
  moveOrderInputSchema.omit({ orderSetId: true }).extend({ kind: z.literal("moveOrder") }),
  resizeStepInputSchema.omit({ orderSetId: true }).extend({ kind: z.literal("resizeStep") }),
  moveStepInputSchema.omit({ orderSetId: true }).extend({ kind: z.literal("moveStep") }),
  setOrderSetEnvelopeInputSchema.omit({ orderSetId: true }).extend({ kind: z.literal("setEnvelope") }),
  resetTimingInputSchema.omit({ orderSetId: true }).extend({ kind: z.literal("resetTiming") }),
  replaceOrderPlannedTrialsInputSchema.omit({ orderSetId: true }).extend({ kind: z.literal("replacePlannedTrials") }),
]);
export type OrderSetChange = z.infer<typeof orderSetChangeSchema>;
export const applyOrderSetChangesInputSchema = z
  .object({ orderSetId: orderSetIdSchema, changes: z.array(orderSetChangeSchema).min(1) })
  .strict();
export type ApplyOrderSetChangesInput = z.infer<typeof applyOrderSetChangesInputSchema>;
